import type { ApiErrorBody, ApiResponse } from '@content-agents/types';
import { env } from '@/config/env';

/** Error thrown for any non-successful API response, carrying the server envelope. */
export class ApiClientError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiClientError';
    this.status = status;
    this.body = body;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /**
   * The payload as a PLAIN OBJECT — this client serialises it (see doFetch).
   * Passing an already-stringified body sends it twice-encoded, which the API's
   * `express.json({ strict: true })` rejects outright, and the failure looks
   * like a server fault rather than a client one.
   *
   * The type mirrors what the server will accept: objects and arrays only,
   * which makes handing a string here a compile error instead of a 400 nobody
   * notices until production. (`object` rather than `Record<string, unknown>`
   * because an interface has no implicit index signature, so the stricter type
   * would reject every typed request body in the app.)
   */
  body?: object;
  params?: Record<string, string | number | boolean | undefined>;
  /** Skip the automatic 401 → refresh → retry (used by auth endpoints themselves). */
  skipAuthRefresh?: boolean;
}

const CSRF_COOKIE = 'csrf_token';
const CSRF_HEADER = 'X-CSRF-Token';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const REFRESH_PATH = '/api/v1/auth/refresh';

const readCookie = (name: string): string | undefined =>
  document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];

const buildUrl = (path: string, params?: RequestOptions['params']): string => {
  const url = new URL(`${env.apiUrl}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
};

// Single-flight refresh: concurrent 401s share one refresh attempt.
let refreshInFlight: Promise<boolean> | null = null;

const attemptRefresh = async (): Promise<boolean> => {
  refreshInFlight ??= (async () => {
    try {
      const csrf = readCookie(CSRF_COOKIE);
      const res = await fetch(buildUrl(REFRESH_PATH), {
        method: 'POST',
        credentials: 'include',
        headers: csrf ? { [CSRF_HEADER]: csrf } : {},
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // Reset after the microtask so all awaiters observe the same result first.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
};

const doFetch = async (path: string, options: RequestOptions): Promise<Response> => {
  const { body, params, headers, method = 'GET', ...rest } = options;
  const csrf = readCookie(CSRF_COOKIE);

  return fetch(buildUrl(path, params), {
    ...rest,
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(!SAFE_METHODS.has(method) && csrf ? { [CSRF_HEADER]: csrf } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
};

const parse = async <T>(response: Response): Promise<T> => {
  const text = await response.text();
  const payload = (text ? JSON.parse(text) : undefined) as ApiResponse<T> | undefined;

  if (!response.ok || !payload || payload.ok === false) {
    const errorBody: ApiErrorBody =
      payload && payload.ok === false
        ? payload.error
        : { code: 'NETWORK_ERROR', message: response.statusText || 'Request failed' };
    throw new ApiClientError(response.status, errorBody);
  }
  return payload.data;
};

/**
 * Typed fetch wrapper. Sends cookies, injects the CSRF header on mutations, and
 * transparently refreshes the access token once on a 401 before retrying.
 */
export const apiRequest = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  let response = await doFetch(path, options);

  if (response.status === 401 && !options.skipAuthRefresh && path !== REFRESH_PATH) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      response = await doFetch(path, options);
    }
  }

  return parse<T>(response);
};
